import { Fragment } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Textarea, TextInput } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import { FEEDBACK_TYPES_IN_ORDER, FeedbackCreateDTO } from '@/models/Feedback';
import { SupportedLanguage } from '@/models/TranslatableField';

type QuestionFeedbacksSectionProps = {
  activeLanguage: SupportedLanguage;
};

type FormShape = { feedbacks: FeedbackCreateDTO[] };

export function QuestionFeedbacksSection({
  activeLanguage,
}: QuestionFeedbacksSectionProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.feedbacks',
  });

  const { register, watch } = useFormContext<FormShape>();
  const feedbacks = watch('feedbacks') ?? [];

  return (
    <div className="flex flex-col gap-4">
      {FEEDBACK_TYPES_IN_ORDER.map(type => {
        const index = feedbacks.findIndex(fb => fb.type === type);
        if (index < 0) return <Fragment key={type} />;

        return (
          <Fieldset key={type} label={t(`types.${type}`)}>
            <Fieldset.Label>{t(`descriptions.${type}`)}</Fieldset.Label>
            <TextInput
              key={`${type}-${activeLanguage}-title`}
              type="text"
              placeholder={t(`titlePlaceholders.${type}`)}
              className="w-full"
              {...register(
                `feedbacks.${index}.title.${activeLanguage}` as const,
              )}
            />
            <Textarea
              key={`${type}-${activeLanguage}-message`}
              placeholder={t(`messagePlaceholders.${type}`)}
              className="w-full"
              rows={4}
              {...register(
                `feedbacks.${index}.message.${activeLanguage}` as const,
              )}
            />
          </Fieldset>
        );
      })}
    </div>
  );
}
